/**
 * Proposed content revision logic (DM-7214).
 *
 * An edit to a live route is stored on the server as the item's proposed
 * revision. Everything this console shows and offers for one is derived here
 * from server data (the item's `pending_revision` summary and its approval
 * request), never from page state, so a reload, another device or the next
 * day all land on the same screen with the same action.
 *
 * Pure and framework-free for the same reason as approvalRequests.logic.ts:
 * it is the part of the flow the jest suite can actually test.
 */

import type { ApprovalRequest, ApprovalRequestStatus } from '../api/approvalRequests';
import type {
  ContentItem,
  ContentRevision,
  ContentRevisionProposal,
  ContentRevisionSummary,
  ContentUpdates,
} from '../api/content';
import type { ChipTone } from '../components/ui/StatusChip';
import { effectiveStatus } from './approvalRequests.logic';

// ---------------------------------------------------------------------------
// Row state
// ---------------------------------------------------------------------------

/**
 * What the row's primary revision button does:
 *   view      - open the request (waiting, or somebody else's)
 *   publish   - approved and mine: publish what the server stored
 *   revise    - rejected: reopen my text in the editor
 *   rerequest - request expired/withdrawn/missing: ask again, same text
 *   rebase    - live item moved on: reopen my text on the new version
 */
export type RevisionAction = 'view' | 'publish' | 'revise' | 'rerequest' | 'rebase';

export interface RevisionRowState {
  chip: { label: string; tone: ChipTone };
  action: RevisionAction;
  actionLabel: string;
  /** One line under the row explaining where the revision stands. */
  note: string;
  mine: boolean;
  /** Offer "Discard revision": the author always may; anyone may clear a dead one. */
  canDiscard: boolean;
  status: ApprovalRequestStatus | 'MISSING';
}

function requestStatus(
  summary: ContentRevisionSummary,
  requests: readonly ApprovalRequest[],
  nowIso: string
): ApprovalRequestStatus | 'MISSING' {
  const request = requests.find(candidate => candidate.id === summary.request_id);
  return request ? effectiveStatus(request, nowIso) : 'MISSING';
}

const DEAD: ReadonlyArray<ApprovalRequestStatus | 'MISSING'> = [
  'EXPIRED',
  'WITHDRAWN',
  'COMPLETED',
  'MISSING',
];

export function revisionRowState({
  item,
  requests,
  viewerSub,
  nowIso,
}: {
  item: Pick<ContentItem, 'pending_revision'>;
  requests: readonly ApprovalRequest[];
  viewerSub: string | undefined;
  nowIso: string;
}): RevisionRowState | null {
  const summary = item.pending_revision;
  if (!summary) return null;

  const status = requestStatus(summary, requests, nowIso);
  const mine = viewerSub !== undefined && summary.author_id === viewerSub;
  const base = { mine, status };

  if (summary.stale) {
    return {
      ...base,
      chip: { label: 'Revision stale', tone: 'crit' },
      action: mine ? 'rebase' : 'view',
      actionLabel: mine ? 'Rebase revision' : 'View revision',
      note: mine
        ? 'The live route changed after you proposed this. Rebase your text onto the current version and request approval again.'
        : 'The live route changed after this revision was proposed. Its author has to rebase it, or it can be discarded.',
      canDiscard: true,
    };
  }
  if (status === 'REJECTED') {
    return {
      ...base,
      chip: { label: 'Revision rejected', tone: 'crit' },
      action: mine ? 'revise' : 'view',
      actionLabel: mine ? 'Revise and resubmit' : 'View revision',
      note: mine
        ? 'A second operator rejected this revision. Your text is kept: revise it and request approval again.'
        : 'A second operator rejected this revision.',
      canDiscard: true,
    };
  }
  if (DEAD.includes(status)) {
    return {
      ...base,
      chip: { label: 'Revision needs approval', tone: 'neutral' },
      action: mine ? 'rerequest' : 'view',
      actionLabel: mine ? 'Request approval again' : 'View revision',
      note: mine
        ? 'The approval request for this revision is no longer open. Your text is kept: request approval again without retyping it.'
        : 'The approval request for this revision is no longer open.',
      canDiscard: true,
    };
  }
  if (status === 'APPROVED') {
    return {
      ...base,
      chip: { label: mine ? 'Approved, ready to publish' : 'Revision approved', tone: 'ok' },
      action: mine ? 'publish' : 'view',
      actionLabel: mine ? 'Publish revision' : 'View revision',
      note: mine
        ? 'A second operator approved exactly this content. Publish it whenever you are ready; the approval is valid for 7 days.'
        : 'Approved. The operator who proposed it publishes it from their console.',
      canDiscard: mine,
    };
  }
  return {
    ...base,
    chip: { label: 'Revision awaiting approval', tone: 'warn' },
    action: 'view',
    actionLabel: mine ? 'View request' : 'Review revision',
    note: mine
      ? 'Stored on the server and waiting for a second operator. You can close this page; nothing is lost.'
      : 'Waiting for a second operator. Review the diff on the Approvals page.',
    canDiscard: mine,
  };
}

/**
 * The plain publish / "Set current" button re-stamps the live item as it is.
 * While a revision is proposed that is never what the operator means, and it
 * is how staging republished unchanged text on 2026-09-21, so it is off.
 */
export function canSetCurrent(item: Pick<ContentItem, 'pending_revision' | 'status'>): boolean {
  return item.status !== 'ARCHIVED' && !item.pending_revision;
}

/**
 * Another operator's revision whose request is still open: the server refuses
 * a second proposal, so say why up front instead of surfacing a 409.
 */
export function editBlockedBy(state: RevisionRowState | null): string | null {
  if (!state || state.mine) return null;
  if (state.status !== 'PENDING' && state.status !== 'APPROVED') return null;
  return state.canDiscard
    ? "Another operator's revision of this route is stale but still open. Discard it to edit this route."
    : 'Another operator has a revision of this route awaiting approval or publish. Wait for it, or ask them to discard it.';
}

/** The request body that re-proposes a stored proposal unchanged. */
export function proposalToUpdates(
  proposal: ContentRevisionProposal,
  changeNote?: string
): ContentUpdates {
  const updates: ContentUpdates = { fields: proposal.fields, tags: proposal.tags };
  if (proposal.route_display_name !== undefined)
    updates.routeDisplayName = proposal.route_display_name;
  if (proposal.route_path !== undefined) updates.routePath = proposal.route_path;
  if (proposal.route_mode !== undefined) updates.routeMode = proposal.route_mode;
  if (proposal.route_version !== undefined) updates.routeVersion = proposal.route_version;
  if (proposal.version_query_params !== undefined) {
    updates.versionQueryParams = proposal.version_query_params;
  }
  if (proposal.screen_id !== undefined) updates.screenId = proposal.screen_id;
  if (changeNote) updates.changeNote = changeNote;
  return updates;
}

/**
 * The editor only has inputs for some of a proposal (name, path, mode,
 * version settings, fields). When a stored revision is revised or rebased,
 * whatever it carries that the editor cannot show has to ride along, or the
 * replacement silently falls back to the live values and the stored change
 * is lost. Everything the editor did set wins.
 */
export function carryUneditedProposalFields(
  updates: ContentUpdates,
  stored: Pick<ContentRevision, 'proposal' | 'change_note'> | null | undefined
): ContentUpdates {
  if (!stored) return updates;
  const carried: ContentUpdates = { tags: stored.proposal.tags };
  if (stored.proposal.screen_id !== undefined) carried.screenId = stored.proposal.screen_id;
  if (stored.change_note) carried.changeNote = stored.change_note;
  return { ...carried, ...updates };
}

/**
 * Approved revisions the viewer proposed and has not published yet. Nothing
 * notifies a requester that a second operator approved, so the sidebar says it.
 */
export function readyToPublishCount(
  requests: readonly ApprovalRequest[] | undefined,
  viewerSub: string | undefined,
  nowIso: string
): number {
  if (!requests || viewerSub === undefined) return 0;
  return requests.filter(
    request =>
      request.operation === 'publishContentRevision' &&
      request.requester_id === viewerSub &&
      effectiveStatus(request, nowIso) === 'APPROVED'
  ).length;
}

// ---------------------------------------------------------------------------
// Diff: what the second operator actually reviews
// ---------------------------------------------------------------------------

export interface DiffLine {
  kind: 'same' | 'add' | 'remove';
  text: string;
}

export interface DiffSkip {
  kind: 'skip';
  count: number;
}

export interface FieldChange {
  /** `fields.content`, `route_path`, ... */
  path: string;
  kind: 'added' | 'removed' | 'changed';
  before?: string;
  after?: string;
  /** Set for long or multi-line text, where a line diff reads better than two blobs. */
  lines?: Array<DiffLine | DiffSkip>;
}

/** Above this many cells the LCS table is not worth building in a browser tab. */
const MAX_DIFF_CELLS = 4_000_000;
const CONTEXT_LINES = 3;
const LONG_TEXT = 120;

function lcsTable(before: readonly string[], after: readonly string[]): Uint32Array[] {
  const table: Uint32Array[] = [];
  for (let row = 0; row <= before.length; row += 1) {
    table.push(new Uint32Array(after.length + 1));
  }
  for (let row = before.length - 1; row >= 0; row -= 1) {
    for (let col = after.length - 1; col >= 0; col -= 1) {
      table[row][col] =
        before[row] === after[col]
          ? table[row + 1][col + 1] + 1
          : Math.max(table[row + 1][col], table[row][col + 1]);
    }
  }
  return table;
}

/** Line diff by longest common subsequence; removals are listed before additions. */
export function diffLines(beforeText: string, afterText: string): DiffLine[] {
  const before = beforeText.split('\n');
  const after = afterText.split('\n');
  if (before.length * after.length > MAX_DIFF_CELLS) {
    return [
      ...before.map(text => ({ kind: 'remove' as const, text })),
      ...after.map(text => ({ kind: 'add' as const, text })),
    ];
  }

  const table = lcsTable(before, after);
  const lines: DiffLine[] = [];
  let row = 0;
  let col = 0;
  while (row < before.length && col < after.length) {
    if (before[row] === after[col]) {
      lines.push({ kind: 'same', text: before[row] });
      row += 1;
      col += 1;
    } else if (table[row + 1][col] >= table[row][col + 1]) {
      lines.push({ kind: 'remove', text: before[row] });
      row += 1;
    } else {
      lines.push({ kind: 'add', text: after[col] });
      col += 1;
    }
  }
  for (; row < before.length; row += 1) lines.push({ kind: 'remove', text: before[row] });
  for (; col < after.length; col += 1) lines.push({ kind: 'add', text: after[col] });
  return lines;
}

/** Keep a few unchanged lines around every change and fold the rest, like a unified diff. */
export function collapseContext(
  lines: readonly DiffLine[],
  context = CONTEXT_LINES
): Array<DiffLine | DiffSkip> {
  const keep = new Array<boolean>(lines.length).fill(false);
  lines.forEach((line, index) => {
    if (line.kind === 'same') return;
    const from = Math.max(0, index - context);
    const to = Math.min(lines.length - 1, index + context);
    for (let cursor = from; cursor <= to; cursor += 1) keep[cursor] = true;
  });

  const collapsed: Array<DiffLine | DiffSkip> = [];
  let skipped = 0;
  lines.forEach((line, index) => {
    if (keep[index]) {
      if (skipped > 0) collapsed.push({ kind: 'skip', count: skipped });
      skipped = 0;
      collapsed.push(line);
    } else {
      skipped += 1;
    }
  });
  if (skipped > 0) collapsed.push({ kind: 'skip', count: skipped });
  return collapsed;
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value === null || typeof value !== 'object') return value;
  const source = value as Record<string, unknown>;
  return Object.keys(source)
    .sort()
    .reduce<Record<string, unknown>>((sorted, key) => {
      sorted[key] = sortKeys(source[key]);
      return sorted;
    }, {});
}

/** How a value is shown and compared: strings as they are, everything else as stable JSON. */
export function renderValue(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(sortKeys(value), null, 2);
}

function isLongText(text: string | undefined): boolean {
  return text !== undefined && (text.includes('\n') || text.length > LONG_TEXT);
}

function changeFor(path: string, before: unknown, after: unknown): FieldChange | null {
  const beforeText = before === undefined ? undefined : renderValue(before);
  const afterText = after === undefined ? undefined : renderValue(after);
  if (beforeText === afterText) return null;
  if (beforeText === undefined) return { path, kind: 'added', after: afterText };
  if (afterText === undefined) return { path, kind: 'removed', before: beforeText };
  const change: FieldChange = { path, kind: 'changed', before: beforeText, after: afterText };
  if (isLongText(beforeText) || isLongText(afterText)) {
    change.lines = collapseContext(diffLines(beforeText, afterText));
  }
  return change;
}

/**
 * Every part of a proposal besides `fields`. This list has to cover the whole
 * ContentRevisionProposal: anything published but missing here is content the
 * second operator approves without seeing. contentRevisions.test.ts pins it
 * to the proposal type.
 */
export const ROUTE_SETTINGS = [
  'route_display_name',
  'route_path',
  'route_mode',
  'route_version',
  'version_query_params',
  'screen_id',
  'tags',
] as const;

/**
 * Every difference between the live item and the proposal: route settings
 * first, then each top-level key of `fields`. An empty result means the
 * revision would publish exactly what is already live.
 */
export function diffRevision(
  base: Pick<ContentItem, (typeof ROUTE_SETTINGS)[number] | 'fields'>,
  proposal: ContentRevisionProposal
): FieldChange[] {
  const changes: FieldChange[] = [];
  for (const setting of ROUTE_SETTINGS) {
    const change = changeFor(setting, base[setting], proposal[setting]);
    if (change) changes.push(change);
  }
  const before = base.fields ?? {};
  const after = proposal.fields ?? {};
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
  for (const key of keys) {
    const change = changeFor(`fields.${key}`, before[key], after[key]);
    if (change) changes.push(change);
  }
  return changes;
}

export function diffStats(changes: readonly FieldChange[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const change of changes) {
    for (const line of change.lines ?? []) {
      if (line.kind === 'add') added += 1;
      if (line.kind === 'remove') removed += 1;
    }
  }
  return { added, removed };
}

// ---------------------------------------------------------------------------
// Deciding a revision request
// ---------------------------------------------------------------------------

export interface RevisionDecisionGate {
  /** Rejecting is always possible; approving only for content that was seen and is publishable. */
  allowApprove: boolean;
  note: string | null;
  tone: ChipTone;
}

/**
 * A second operator approves content, not a label. So there is no approving
 * before the diff has loaded, and none for a revision that is gone or that
 * the live route has moved past (the server refuses both as well).
 */
export function revisionDecisionGate({
  hasRevision,
  loading,
  failed,
  stale,
  changeCount,
}: {
  hasRevision: boolean;
  loading: boolean;
  failed: boolean;
  stale: boolean;
  changeCount: number;
}): RevisionDecisionGate {
  if (!hasRevision) return { allowApprove: true, note: null, tone: 'neutral' };
  if (loading) {
    return {
      allowApprove: false,
      note: 'Loading the proposed content. You can only approve what you have seen.',
      tone: 'neutral',
    };
  }
  if (failed) {
    return {
      allowApprove: false,
      note: 'The proposed revision could not be loaded; it may have been replaced or discarded. It cannot be approved. You can still reject the request.',
      tone: 'crit',
    };
  }
  if (stale) {
    return {
      allowApprove: false,
      note: 'The live route changed after this revision was proposed, so it cannot be approved. Reject it, or leave it for its author to rebase.',
      tone: 'crit',
    };
  }
  if (changeCount === 0) {
    return {
      allowApprove: true,
      note: 'This revision is identical to what is live. Publishing it would change nothing.',
      tone: 'warn',
    };
  }
  return { allowApprove: true, note: null, tone: 'neutral' };
}
